/**
 * The product board route's data source (spec M1-B4 §5, round 3 wiring).
 *
 * One `TrpcBoardDataSource` per mounted board: the source caches the board id it last loaded
 * (for "save to this view") and the per-view collapsed set, so a second board must not share
 * one. The table view is written against the `BoardDataSource` interface and never learns
 * which implementation it has — `/__ui/board` keeps the in-memory fixture source, and nothing
 * under `table/` changes either way.
 */
import type { UserId } from '@elizmonday/shared';
import { useMemo } from 'react';
import { useTRPCClient } from '../../../lib/trpc';
import { useBootstrap } from '../../shell/bootstrap';
import { createBoardApi, TrpcBoardDataSource, type TrpcLike } from './trpc-source';
import type { BoardDataSource } from './types';

export function useBoardSource(boardId: string | null): BoardDataSource | null {
  const client = useTRPCClient();
  const me = useBootstrap().data?.me.id ?? null;

  return useMemo(() => {
    if (!boardId || !me) return null;
    // `createTRPCClient`'s proxy is structurally the `TrpcLike` port; typing it here rather
    // than importing the server router keeps `trpc-source.ts` free of server types.
    return new TrpcBoardDataSource(createBoardApi(client as unknown as TrpcLike), me as UserId);
  }, [client, me, boardId]);
}
